import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserService } from '@services/user.service';
import { catchError, Observable, retry, throwError } from 'rxjs';
import { SessionStorageService } from './storage.service';

@Injectable({
	providedIn: 'root',
})
export class MatricesService {
	constructor(
		private httpClient: HttpClient,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
	) {}

	private getHeaders() {
		const accountId: string = this.userService.account.id;
		const token: string = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({
			Authorization: `Bearer ${token}`,
			'x-account-id': accountId,
		});
	}

	getMatricesList(): Observable<IMatrices> {
		const apiUrl = `${environment.awsEnvUri}/matrix/engines/${this.userService.account.defaultEngine}/calculations`;
		return this.httpClient
			.get<IMatrices>(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	private handleError(error: { error: { message: string }; status: any; message: any }) {
		if (error.status === 404) {
			return throwError(() => new Error('Resource not found'));
		}
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}

	public getLastSegment(urlOrObject: string | any, keyPath?: string[]) {
		let url: string;

		if (typeof urlOrObject === 'string') {
			url = urlOrObject;
		} else if (Array.isArray(keyPath)) {
			url = keyPath.reduce((acc, key) => acc[key], urlOrObject);
		} else {
			throw new TypeError('Invalid arguments');
		}

		const parts = url.split('/');
		return parts[parts.length - 1];
	}

	getDownloadUrl(self: string, type: string): Observable<ISignedUrlResource> {
		const extension: string = type === 'json' ? '' : '/raw';
		const apiUrl = `${environment.awsEnvUri + self}/data${extension}`;
		return this.httpClient
			.get<ISignedUrlResource>(apiUrl, {
				headers: this.getHeaders(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}
}

export interface ISignedUrlResource {
	url: string;
	expiration: string;
}

export interface ITableMatrix {
	self: string;
	tags: string;
	createdDateEpoch: number;
	createdDate: string;
	// createdBy: string;
	status: string;
	totalCalculations: number;
	completedCalculations: number;
}

export interface IMatrices {
	self: string;
	items: IResponseMatrix[];
}

export interface IResponseMatrix extends IBaseMatrix, IMeta {
	tags: ITag[];
}

export interface IBaseMatrix {
	self: string;
	status: string;
	totalCalculations: number;
	completedCalculations: number;
}

export interface ITag {
	name: string;
	value: string;
}

export interface IMeta {
	meta: {
		createdAt: number;
		createdBy: {
			self: string;
		};
		updatedAt: number;
		updatedBy: {
			self: string;
		};
		version: number;
	};
}
